import React, { useState, useEffect, useRef, useId } from "react";
import { m, AnimatePresence, useReducedMotion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { track, trackWhatsAppClick, utmLeadFields, whatsappUrlWithContext } from "../../lib/analytics";
import Captcha, { resetCaptcha } from "../../components/Captcha";
import { Button, Badge, Input, Textarea, Label } from "../../components/ui/primitives";
import { cn } from "../../lib/utils";
import { useMarketingMotion } from "../../components/marketing/MarketingMotion";
// WP-14: ONE Explore config — rendered by the desktop dropdown here AND the Landing mobile
// drawer (the hand-written mobile copy this replaces had dropped Pricing and Switch).
import { CTA_PRIMARY_LABEL, CTA_SECONDARY_LABEL, EXPLORE_ITEMS } from "../../components/marketing/navigation";
import BrandWordmarkText, { BrandCopy } from "../../components/BrandWordmarkText";
import { LOGO_FULL } from "../../components/Logo";
import { Users, CalendarCheck, ShieldCheck, ArrowRight, Check, X, Zap, ChevronDown, ChevronRight, User, Clock, Building2, LayoutDashboard, UserPlus, PenLine, Mail, Phone, MapPin, MessageCircle, MessageSquare } from "lucide-react";
import { Select } from "../../components/ui/primitives";
import {
  CTA_GRADIENT,
  LEAD_ROLE_OPTIONS,
  LEAD_STUDENT_BANDS,
  ORANGE_CTA_GRADIENT,
  withLeadRole,
} from "../../components/marketing/shared";

// A11Y-2: the AA-safe warm CTA gradient now LIVES in components/marketing/shared.js beside its
// blue sibling (one gradient authority; the AA math comment moved with it) so the shared chrome
// can use it without a circular chunk pull. Re-exported here for established importers.
export { ORANGE_CTA_GRADIENT, ORANGE_CTA_GRADIENT_HOVER } from "../../components/marketing/shared";

// (The old exported ImageSlot placeholder was deleted 2026-08-18 — repo-wide grep proved it had
// no consumer outside this file, and its latent recipes were never a11y-reviewed. A11Y-10.)


// One repeating crop of the official wordmark artwork — never a font reconstruction. It fills the
// whole hero with almost no DOM; `colorClass` controls only the decorative layer's opacity.
const heroWmPattern = (id, colorClass) => (
  <svg className={cn("absolute inset-0 w-full h-full", colorClass)} aria-hidden="true" focusable="false">
    <defs>
      <pattern id={id} width="224" height="42" patternUnits="userSpaceOnUse" patternTransform="rotate(-8)">
        <svg x="0" y="6" width="211" height="30" viewBox="185 40 486 69" preserveAspectRatio="xMidYMid meet" overflow="hidden">
          <image href={LOGO_FULL} width="671" height="132" />
        </svg>
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#${id})`} />
  </svg>
);

// Hero cursor lens — the brand watermark is essentially invisible by default and only becomes legible inside a
// small convex glass lens that smoothly trails the cursor. Disabled on mobile + under reduced-motion;
// the rAF lerp loop is also stopped whenever the hero section is off-viewport (IntersectionObserver).
// Mounted in the Landing hero (Landing.js).
function HeroCursorLensReveal() {
  const prefersReducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const reduce = prefersReducedMotion || paused;
  const rootRef = useRef(null);
  const [active, setActive] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [inView, setInView] = useState(true);
  const target = useRef({ x: -9999, y: -9999 });
  const smooth = useRef({ x: -9999, y: -9999 });

  // only run on >= md (768px) — the effect is cursor-driven, so it's pointless (and disabled) on touch/mobile
  useEffect(() => {
    const check = () => setEnabled(typeof window !== "undefined" && window.innerWidth >= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Perf (MARKETING_10X_PLAN P1-7): the lerp loop below otherwise runs a rAF every frame for the
  // page's whole lifetime — stop it while the hero is scrolled off-viewport. (Reduced-motion never
  // starts it: the `reduce` gate below + the null render.)
  useEffect(() => {
    if (reduce || !enabled || typeof IntersectionObserver === "undefined") return;
    const section = rootRef.current?.parentElement;
    if (!section) return;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0 });
    io.observe(section);
    return () => io.disconnect();
  }, [reduce, enabled]);

  // track the cursor relative to the parent <section>, lerp one smoothed follower each frame and publish it as
  // --mx/--my on the root (read by the mask, the magnify origin and the lens). One rAF, one var-write per frame.
  useEffect(() => {
    if (reduce || !enabled || !inView) return;
    const root = rootRef.current;
    const section = root?.parentElement;
    if (!root || !section) return;
    let raf = 0, on = false;
    const lerp = (a, b, t) => a + (b - a) * t;
    const onMove = (e) => {
      const r = section.getBoundingClientRect();
      target.current.x = e.clientX - r.left;
      target.current.y = e.clientY - r.top;
      if (!on) { on = true; setActive(true); }
    };
    const onLeave = () => { on = false; setActive(false); };
    const loop = () => {
      smooth.current.x = lerp(smooth.current.x, target.current.x, 0.14);
      smooth.current.y = lerp(smooth.current.y, target.current.y, 0.14);
      root.style.setProperty("--mx", `${smooth.current.x}px`);
      root.style.setProperty("--my", `${smooth.current.y}px`);
      raf = requestAnimationFrame(loop);
    };
    section.addEventListener("mousemove", onMove);
    section.addEventListener("mouseleave", onLeave);
    raf = requestAnimationFrame(loop);
    return () => { section.removeEventListener("mousemove", onMove); section.removeEventListener("mouseleave", onLeave); cancelAnimationFrame(raf); };
  }, [reduce, enabled, inView]);

  if (reduce || !enabled) return null; // clean hero by default on mobile / reduced-motion

  return (
    <div ref={rootRef} className="absolute inset-0 z-0 overflow-hidden pointer-events-none select-none" aria-hidden="true">
      {/* watermark is revealed ONLY through the small lens mask (no always-on base layer) — magnified around
          the cursor for a convex feel. Full hero coverage via the SVG pattern, so it works at the bottom too. */}
      <div className="hero-wm-mask absolute inset-0 transition-opacity duration-500" style={{ opacity: active ? 1 : 0 }}>
        <div className="absolute inset-0" style={{ transformOrigin: "var(--mx, 50%) var(--my, 50%)", transform: "scale(1.14)" }}>
          {heroWmPattern("heroWmReveal", "hero-wm-reveal")}
        </div>
      </div>
      {/* glass lens overlay — small ring + soft glow + faint tint, centred on the cursor */}
      <div className="hero-lens absolute top-0 left-0 rounded-full transition-opacity duration-500"
        style={{ opacity: active ? 1 : 0, transform: "translate(var(--mx, -9999px), var(--my, -9999px)) translate(-50%, -50%)" }} />
    </div>
  );
}

// Register-your-school starter + demo request. Registration is the DEFAULT mode (schools can sign up
// self-serve without a demo call — it hands off to /register with the basics prefilled); the demo form
// stays available behind the toggle and still posts to the public PLATFORM_LEADS pipeline (same as the modal).
// ── Process-guide side panel (opened by the divider arrow between the heading & form) ──────────
// Tab-aware content: register (blue) vs demo (orange). Slides in from the card's left edge, overlays
// the form, closes on backdrop-click / Esc / the close button. Purely an interactive guide — it never
// touches the form fields or submit.
const GUIDE_CONTENT = {
  register: {
    theme: "blue", icon: UserPlus,
    title: "How registration works",
    subtitle: "Create your school workspace in four steps.",
    steps: [
      { icon: Building2, title: "Enter school details", desc: "School name, owner name and work email." },
      { icon: UserPlus, title: "Create school account", desc: "We create your School Code and your first sign-in." },
      { icon: Users, title: "Invite your team", desc: "Add teachers, office staff, parents and students." },
      { icon: LayoutDashboard, title: "Start managing", desc: "Begin using admissions, fees, attendance and communication." },
    ],
    final: { title: "What happens next", desc: "Your school workspace will be created and ready for onboarding." },
    cta: "Continue registration",
  },
  demo: {
    theme: "orange", icon: CalendarCheck,
    title: "How a demo works",
    subtitle: "See how Vidyapeeth360 works for your school.",
    steps: [
      { icon: User, title: "Share your contact details", desc: "Name, mobile number, email and school name." },
      { icon: PenLine, title: "Tell us your requirement", desc: "Add city and optional message." },
      { icon: Clock, title: "Get confirmation", desc: "Our team reviews your request." },
      { icon: CalendarCheck, title: "Next step confirmed", desc: "The team confirms a time and shows you the workflows your school asked about." },
    ],
    final: { title: "Demo request received", desc: "Your request is recorded for review and follow-up." },
    cta: CTA_SECONDARY_LABEL,
  },
};

// Circular (or labeled-pill) arrow trigger. Rotates 180° when the guide is open.
// ALT-001: four triggers used to share ONE aria-label ("Open process guide") across TWO different
// guides, and on the labelled variant that name overrode the visible "See how it works" (WCAG 2.5.3
// Label in Name). The labelled variant now KEEPS its visible words and extends the accessible name
// with an sr-only suffix naming the guide; the icon-only variant names the guide outright.
function GuideTrigger({ open, onClick, theme, className, label, guideTitle }) {
  const blue = theme === "blue";
  return (
    <button type="button" onClick={onClick} aria-label={label ? undefined : `Open the guide: ${guideTitle}`} aria-expanded={open}
      className={cn(
        // DELTA-7: scoped transition list — the trigger only ever changes its transform (hover
        // scale), its border colour and its shadow/ring; `transition-all` watched everything.
        "inline-flex items-center justify-center gap-1.5 rounded-full bg-surface shadow-sm transition-[transform,border-color,box-shadow] duration-200 hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        label ? "px-4 h-9 text-[13px] font-semibold" : "w-8 h-8",
        blue
          ? "border border-primary/40 text-primary hover:border-primary hover:shadow-[0_6px_16px_-4px_rgba(29,78,216,0.45)] focus-visible:ring-primary/40"
          : "border border-orange-border/70 text-orange-text hover:border-orange hover:shadow-[0_6px_16px_-4px_rgba(249,115,22,0.45)] focus-visible:ring-orange/40",
        className
      )}>
      {label && <span>{label}<span className="sr-only">: {guideTitle}</span></span>}
      <ChevronRight className={cn("w-4 h-4 transition-transform duration-300", open && "rotate-180")} aria-hidden="true" />
    </button>
  );
}

// The sliding guide panel — absolute inside the QuickEnquiry card. `onCta` focuses the first empty field.
function ProcessGuidePanel({ open, onClose, onCta, mode }) {
  const reduce = useReducedMotion();
  const closeRef = useRef(null);
  const openerRef = useRef(null);
  const g = GUIDE_CONTENT[mode] || GUIDE_CONTENT.register;
  const blue = g.theme === "blue";
  useEffect(() => {
    if (!open) return undefined;
    // A11Y-5: a real modal contract. Remember the invoking GuideTrigger so every close path
    // (Esc / backdrop / X) restores focus to it — the CTA path deliberately overrides this by
    // focusing the first empty form field 140ms later (guideCta), which wins over the rAF below.
    const opener = document.activeElement;
    openerRef.current = opener instanceof HTMLElement && opener !== document.body ? opener : null;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => closeRef.current?.focus(), 80);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
      const returnTarget = openerRef.current;
      openerRef.current = null;
      if (returnTarget?.isConnected) window.requestAnimationFrame(() => returnTarget.focus());
    };
  }, [open, onClose]);
  // A11Y-5: trap Tab inside the dialog (the AiraAssistant WP-17 trap) — aria-modal="true" tells
  // AT the page behind the backdrop is inert, so keyboard focus must never walk into it.
  const trapTab = (e) => {
    if (e.key !== "Tab") return;
    const panel = e.currentTarget;
    const focusables = panel.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  return (
    <AnimatePresence>
      {open && (
        <>
          <m.div className="absolute inset-0 z-40 rounded-[28px] bg-royal/25 backdrop-blur-[2px]" aria-hidden="true"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onClick={onClose} />
          <m.aside role="dialog" aria-modal="true" aria-label={g.title} onKeyDown={trapTab}
            className="absolute left-0 top-0 bottom-0 z-50 flex w-[90vw] sm:w-[340px] lg:w-[410px] max-w-full flex-col overflow-hidden rounded-r-[24px] border border-[rgba(15,23,42,0.08)] bg-surface/95 backdrop-blur-xl shadow-[0_24px_70px_rgba(15,23,42,0.16)]"
            initial={reduce ? { opacity: 0 } : { x: "-100%" }} animate={reduce ? { opacity: 1 } : { x: 0 }} exit={reduce ? { opacity: 0 } : { x: "-100%" }}
            transition={reduce ? { duration: 0.2 } : { type: "tween", duration: 0.34, ease: [0.22, 1, 0.36, 1] }}>
            <div className={cn("pointer-events-none absolute -top-12 -right-10 w-44 h-44 rounded-full blur-3xl", blue ? "bg-primary/15" : "bg-orange/15")} aria-hidden="true" />
            {/* header */}
            <div className="relative flex items-start gap-3 border-b border-line p-5">
              <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", blue ? "bg-primary/10 text-primary" : "bg-orange/10 text-orange")}><g.icon className="h-5 w-5" aria-hidden="true" /></span>
              <div className="min-w-0 flex-1">
                <h3 className="font-heading text-[17px] font-bold leading-tight text-ink">{g.title}</h3>
                <p className="mt-1 text-[13px] leading-snug text-ink-2"><BrandCopy>{g.subtitle}</BrandCopy></p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label={`Close the guide: ${g.title}`}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-subtle transition-colors hover:bg-muted hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"><X className="h-4 w-4" /></button>
            </div>
            {/* numbered steps (connected vertical line) + final response */}
            <div className="relative flex-1 overflow-y-auto p-5">
              <ol className="relative space-y-4">
                <span className={cn("absolute left-[15px] top-3 bottom-8 w-px", blue ? "bg-primary/20" : "bg-orange/25")} aria-hidden="true" />
                {g.steps.map((s, i) => (
                  <li key={s.title} className="relative flex gap-3.5">
                    <span className={cn("relative z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full text-[13px] font-bold ring-4 ring-surface", blue ? "bg-primary/10 text-primary" : "bg-orange/10 text-orange-text")}>{i + 1}</span>
                    <div className="min-w-0 pt-0.5">
                      <p className="flex items-center gap-1.5 text-[14px] font-semibold leading-tight text-ink"><s.icon className={cn("h-3.5 w-3.5 shrink-0", blue ? "text-primary" : "text-orange")} aria-hidden="true" />{s.title}</p>
                      <p className="mt-0.5 text-[12.5px] leading-snug text-ink-2">{s.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <div className={cn("mt-5 flex items-start gap-3 rounded-2xl border p-4", blue ? "border-primary/15 bg-primary/[0.06]" : "border-orange-border/50 bg-orange-soft")}>
                <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-full", blue ? "bg-primary/15 text-primary" : "bg-orange/15 text-orange")}><Check className="h-5 w-5" strokeWidth={2.5} aria-hidden="true" /></span>
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold leading-tight text-ink">{g.final.title}</p>
                  <p className="mt-0.5 text-[12.5px] leading-snug text-ink-2">{g.final.desc}</p>
                </div>
              </div>
            </div>
            {/* CTA — focuses the first empty field in the active form. The orange branch uses the
                AA-proven ORANGE_CTA_GRADIENT (>=4.5:1 at every stop — see the math at the top). */}
            <div className="relative border-t border-line p-5">
              <button type="button" onClick={onCta} data-testid="guide-cta"
                // DELTA-7: scoped transition list — this CTA changes transform (lift/press) and
                // its focus ring only; its gradient fill is static.
                className={cn("inline-flex h-11 w-full items-center justify-center gap-2 rounded-full text-[14px] font-semibold text-white transition-[transform,box-shadow] duration-200 hover:-translate-y-px active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                  blue ? `${CTA_GRADIENT} shadow-[0_10px_24px_rgba(29,78,216,0.22)] focus-visible:ring-primary/50` : `${ORANGE_CTA_GRADIENT} shadow-[0_10px_24px_rgba(154,52,18,0.28)] focus-visible:ring-orange-dark/50`)}>
                {g.cta} <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </m.aside>
        </>
      )}
    </AnimatePresence>
  );
}

const isBookDemoIntent = (location) => {
  const params = new URLSearchParams(location?.search || "");
  return params.get("intent") === "book-demo";
};

function QuickEnquiry() {
  const navigate = useNavigate();
  const location = useLocation();
  const bookDemoIntent = isBookDemoIntent(location);
  const [mode, setMode] = useState(() => (bookDemoIntent ? "demo" : "register")); // "register" | "demo"
  const [f, setF] = useState({ contact_name: "", email: "", phone: "", school_name: "", city: "", message: "" });
  // CRO-8: light qualification — both optional, single row, never lengthens the required path.
  const [studentBand, setStudentBand] = useState("");
  const [role, setRole] = useState("");
  const [r, setR] = useState({ school_name: "", owner_name: "", email: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(""); // server/submit errors only — validation is per-field below
  // A11Y-6: per-field errors (aria-invalid + aria-describedby + focus-to-first-invalid), the
  // pattern ContactSalesModal already implements. Keys match the form state keys.
  const [fieldErrors, setFieldErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState("");
  const reduce = useReducedMotion();
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setFieldErrors((er) => (er[k] ? { ...er, [k]: "" } : er)); };
  const setReg = (k, v) => { setR((p) => ({ ...p, [k]: v })); setFieldErrors((er) => (er[k] ? { ...er, [k]: "" } : er)); };
  // Process-guide side panel (opened by the divider arrow). Content follows the active tab.
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const openGuide = () => setIsGuideOpen(true);
  const closeGuide = () => setIsGuideOpen(false);
  // (param intentionally NOT named `m` — that identifier is the framer-motion component set, PERF-6)
  const focusFirstEmpty = (formMode) => {
    const ids = formMode === "register" ? ["lr-school", "lr-name", "lr-email"] : ["qe-name", "qe-email", "qe-phone", "qe-inst", "qe-city"];
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    (els.find((el) => !String(el.value || "").trim()) || els[0])?.focus();
  };
  const guideCta = () => { setIsGuideOpen(false); setTimeout(() => focusFirstEmpty(mode), 140); };
  // Nav/hero "Book Demo" CTAs scroll here AND flip the section to the demo form.
  useEffect(() => {
    const openDemo = () => { setMode("demo"); setErr(""); setFieldErrors({}); };
    window.addEventListener("vp-open-demo-form", openDemo);
    return () => window.removeEventListener("vp-open-demo-form", openDemo);
  }, []);
  // Cross-page CTAs use a canonical URL rather than a page-local event. Resolve that intent both
  // on the first render and on client-side/back-forward navigation so the destination never lands
  // on the registration tab by mistake.
  useEffect(() => {
    if (!bookDemoIntent) return;
    setMode("demo");
    setErr("");
  }, [bookDemoIntent]);
  // staggered entrance for the form fields
  const fieldItem = { hidden: reduce ? {} : { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } } };
  // A11Y-6: mark each invalid field + move focus to the first one (DOM order).
  const applyFieldErrors = (next, order) => {
    setFieldErrors(next);
    const first = order.find(([key]) => next[key]);
    if (first) document.getElementById(first[1])?.focus();
    return !first;
  };
  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!f.contact_name.trim()) next.contact_name = "Enter your full name.";
    if (!f.email.trim() || !/^\S+@\S+\.\S+$/.test(f.email.trim())) next.email = "Enter a valid work email, such as name@school.edu.";
    if (!f.school_name.trim()) next.school_name = "Enter your school's name.";
    if (!applyFieldErrors(next, [["contact_name", "qe-name"], ["email", "qe-email"], ["school_name", "qe-inst"]])) return;
    setErr(""); setBusy(true);
    try {
      // utmLeadFields → the backend's whitelisted `campaign` field (attribution); spread FIRST so it can't clobber form keys
      await api.post("/public/platform-enquiry", {
        ...utmLeadFields(),
        ...f,
        // CRO-8: the optional role rides in `message` (the enquiry model forbids extra keys);
        // the band value is the band ceiling — an approximate size signal, not a head count.
        // One fold shape for all three lead forms (components/marketing/shared.js).
        message: withLeadRole(f.message, role),
        ...(studentBand ? { student_count: Number(studentBand) } : {}),
        source: "demo:landing",
        captcha_token: captchaToken || undefined,
      });
      // Opening a demo surface records `demo_request`; a successful POST records the distinct
      // conversion event below. Re-emitting `demo_request` here double-counted one intent.
      track("demo_form_submitted", { placement: "landing_demo_form" }); // WP-06 canonical conversion
      setSent(true);
    } catch (e2) {
      // CRO-5: a prod form outage must be analytically visible, not silent.
      track("lead_submit_failed", { placement: "landing_demo_form", http_status: e2?.response?.status ?? 0 });
      setErr(e2.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { resetCaptcha(); setBusy(false); }
  };
  // Registration starter: validate the basics, then hand off to the real /register flow prefilled.
  const startRegistration = (e) => {
    e.preventDefault();
    const next = {};
    if (!r.school_name.trim()) next.school_name = "Enter your school's name.";
    if (!r.owner_name.trim()) next.owner_name = "Enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(r.email.trim())) next.email = "Enter a valid work email to create your account.";
    if (!applyFieldErrors(next, [["school_name", "lr-school"], ["owner_name", "lr-name"], ["email", "lr-email"]])) return;
    setErr("");
    // CRO-2: `trial_start` is reserved for a COMPLETED registration (Register.js); every
    // Start-free-trial CLICK fires trial_cta_click with a distinct placement instead.
    track("trial_cta_click", { placement: "landing_quick_form" });
    const q = new URLSearchParams({ school_name: r.school_name.trim(), owner_name: r.owner_name.trim(), email: r.email.trim() });
    navigate(`/register?${q.toString()}`);
  };
  const isReg = mode === "register";
  return (
    <section id="see-vidyapeeth360-in-action" className="relative bg-muted border-b border-line overflow-hidden scroll-mt-24" aria-label="Register your school or book a demo">
      {/* hero "Register" CTA scroll target */}
      <span id="register-your-school" aria-hidden="true" className="absolute top-0 left-0 h-0 w-0 scroll-mt-24" />
      {/* premium background — subtle grid + settled blue/orange glows + dotted accent */}
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" aria-hidden="true" />
      <div className="absolute -top-16 right-4 w-80 h-80 rounded-full bg-primary/12 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute -bottom-16 left-2 w-72 h-72 rounded-full bg-orange/10 blur-3xl pointer-events-none" aria-hidden="true" />
      <span className="hero-dotgrid absolute top-7 right-10 hidden md:block opacity-70" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-5 py-10 md:py-12 relative">
        <m.div
          className="relative rounded-[28px] border border-primary/10 bg-surface/90 backdrop-blur-xl shadow-[0_24px_80px_rgba(15,23,42,0.10)] p-6 sm:p-7 lg:p-8 overflow-hidden"
          initial={reduce ? false : { opacity: 0, y: 40 }}
          whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          whileHover={reduce ? undefined : { y: -3 }}
        >
          {/* thin top highlight line */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent" aria-hidden="true" />

          {sent ? (
            <m.div initial={reduce ? false : { opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="rounded-2xl border border-positive/30 bg-positive-soft px-5 py-6" data-testid="enquiry-sent">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-xl bg-positive/15 grid place-items-center shrink-0"><Check className="w-5 h-5 text-positive" aria-hidden="true" /></span>
                <div><p className="font-heading font-semibold text-ink text-lg">Thanks — we've got your request!</p><p className="text-ink-2 text-sm mt-1">Our team will get back to you — we reply on working days.</p></div>
              </div>
              {/* CRO-4 parity with ContactSalesModal's sent state: real next steps instead of a
                  dead-end — the self-serve trial and the verified WhatsApp channel. */}
              <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
                <Link to="/register" onClick={() => track("trial_cta_click", { placement: "landing_demo_sent" })} data-testid="enquiry-sent-trial"
                  className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-full ${CTA_GRADIENT} px-5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(29,78,216,0.22)] transition-[transform,box-shadow] duration-150 hover:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2`}>
                  {CTA_PRIMARY_LABEL} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
                <a href={whatsappUrlWithContext("the demo request form")} target="_blank" rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick("demo_request_sent")} data-testid="enquiry-sent-whatsapp"
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-line bg-surface px-4 text-sm font-semibold text-ink-2 transition-[background-color,border-color,color] duration-150 hover:border-accent/30 hover:bg-muted hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/55">
                  <MessageCircle className="w-4 h-4 text-positive" aria-hidden="true" /> WhatsApp our team
                </a>
              </div>
              <p className="mt-3 text-xs leading-5 text-ink-subtle">The trial is self-serve and doesn't wait on this request.</p>
            </m.div>
          ) : (
            <>
            {/* mode toggle — register (default, self-serve) vs demo */}
            <div className="flex justify-center mb-7">
              <div className="inline-flex items-center rounded-full border border-line bg-muted p-1 gap-1" role="group" aria-label="Choose how to get started">
                <button type="button" aria-pressed={isReg} onClick={() => { setMode("register"); setErr(""); setFieldErrors({}); }} data-testid="landing-mode-register"
                  className={cn("rounded-full px-4 sm:px-5 py-2 text-sm font-semibold transition-colors inline-flex items-center gap-1.5",
                    isReg ? "bg-surface text-ink shadow-sm border border-line" : "text-ink-2 hover:text-ink")}>
                  <UserPlus className="w-3.5 h-3.5" aria-hidden="true" /> Register your school
                </button>
                <button type="button" aria-pressed={!isReg} onClick={() => { setMode("demo"); setErr(""); setFieldErrors({}); }} data-testid="landing-mode-demo"
                  className={cn("rounded-full px-4 sm:px-5 py-2 text-sm font-semibold transition-colors inline-flex items-center gap-1.5",
                    !isReg ? "bg-surface text-ink shadow-sm border border-line" : "text-ink-2 hover:text-ink")}>
                  <CalendarCheck className="w-3.5 h-3.5" aria-hidden="true" /> Book a free demo
                </button>
              </div>
            </div>

            {isReg ? (
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-0 items-center">
              {/* LEFT — registration pitch */}
              <m.div className="lg:pr-12" initial={reduce ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: "easeOut", delay: 0.05 }}>
                <Badge tone="blue" className="mb-3"><UserPlus className="w-3 h-3" aria-hidden="true" /> Start free — no demo required</Badge>
                <h2 className="font-heading font-bold text-[24px] sm:text-[30px] text-ink tracking-tight leading-tight">Register your school <span className="text-accent">in minutes</span></h2>
                <span className="mt-2.5 block h-1 w-16 rounded-full bg-gradient-to-r from-accent to-accent/20" aria-hidden="true" />
                <p className="text-ink-2 text-sm mt-3.5 leading-normal">Create your school's workspace yourself — pick your School Code, then invite your staff and set up admissions, fees, attendance and communication. Vidyapeeth360 reviews new school registrations on working days before your workspace opens.</p>
                <GuideTrigger theme="blue" open={isGuideOpen} onClick={openGuide} label="See how it works" guideTitle={GUIDE_CONTENT.register.title} className="lg:hidden mt-4" />
              </m.div>

              {/* RIGHT — registration starter; hands off to /register with these basics prefilled */}
              <form onSubmit={startRegistration} noValidate className="relative lg:pl-12 lg:border-l lg:border-accent/20">
                <GuideTrigger theme="blue" open={isGuideOpen} onClick={openGuide} guideTitle={GUIDE_CONTENT.register.title} className="hidden lg:inline-flex absolute -left-4 top-1/2 -translate-y-1/2 z-30" />
                <m.div className="grid sm:grid-cols-2 gap-x-5 gap-y-4"
                  variants={{ hidden: {}, show: { transition: { staggerChildren: reduce ? 0 : 0.07, delayChildren: reduce ? 0 : 0.12 } } }}
                  initial="hidden" animate="show">
                  {[
                    { Icon: Building2, id: "lr-school", label: "School name", ph: "e.g. your school's name", val: r.school_name, key: "school_name", tid: "landing-reg-school", span: true },
                    { Icon: User, id: "lr-name", label: "Your full name", ph: "You'll be the owner / admin", val: r.owner_name, key: "owner_name", tid: "landing-reg-name" },
                    { Icon: Mail, id: "lr-email", label: "Work email", type: "email", ph: "Enter your email", val: r.email, key: "email", tid: "landing-reg-email" },
                  ].map(({ Icon, id, label, type, ph, val, key, tid, span }) => (
                    <m.div key={id} variants={fieldItem} className={span ? "sm:col-span-2" : undefined}>
                      <Label htmlFor={id} className="mb-2 font-semibold text-ink">{label}<span className="text-danger ml-0.5" aria-hidden="true">*</span></Label>
                      <div className="relative">
                        <Icon className="w-4 h-4 text-accent/60 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
                        <Input id={id} className="h-12 pl-11" type={type} value={val} onChange={(e) => setReg(key, e.target.value)} placeholder={ph} data-testid={tid} required
                          aria-invalid={fieldErrors[key] ? true : undefined} aria-describedby={fieldErrors[key] ? `${id}-err` : undefined} />
                      </div>
                      {fieldErrors[key] && <p id={`${id}-err`} role="alert" className="mt-1.5 text-xs font-medium text-danger">{fieldErrors[key]}</p>}
                    </m.div>
                  ))}
                </m.div>
                <m.div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mt-5" initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: reduce ? 0 : 0.4 }}>
                  {err ? <span className="text-xs text-danger" role="alert">{err}</span>
                       : <span className="text-xs text-ink-2">Next: pick your School Code and a password.</span>}
                  <Button type="submit" className="group !rounded-full !px-7 shadow-[0_10px_32px_-6px_rgba(29,78,216,0.5)] hover:-translate-y-0.5 duration-300" data-testid="landing-reg-cta">Create your school account <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></Button>
                </m.div>
              </form>
            </div>
            ) : (
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-0 items-center">
              {/* LEFT — badge + heading + subtitle (compact; trust points moved to the footer row) */}
              <m.div className="lg:pr-12" initial={reduce ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: "easeOut", delay: 0.05 }}>
                {/* A11Y-3: no colour override — the Badge orange default (text-orange-dark #C2410C
                    on bg-orange-soft, ~4.8:1) is the AA-safe recipe for this 12px/600 text. */}
                <Badge tone="orange" className="mb-3"><CalendarCheck className="w-3 h-3" aria-hidden="true" /> Book a free demo</Badge>
                <h2 className="font-heading font-bold text-[24px] sm:text-[30px] text-ink tracking-tight leading-tight">See <BrandWordmarkText /> <span className="text-orangeWarm-600 dark:text-orangeWarm-400">in Action</span></h2>
                <span className="mt-2.5 block h-1 w-16 rounded-full bg-gradient-to-r from-orange to-orange/20" aria-hidden="true" />
                <p className="text-ink-2 text-sm mt-3.5 leading-normal">Get a guided walkthrough and see how your school can manage admissions, fees, attendance, exams and communication from one platform.</p>
                <GuideTrigger theme="orange" open={isGuideOpen} onClick={openGuide} label="See how it works" guideTitle={GUIDE_CONTENT.demo.title} className="lg:hidden mt-4" />
              </m.div>

              {/* RIGHT — form with staggered field entrance; divider + chevron echo the two-panel layout */}
              <form onSubmit={submit} noValidate className="relative lg:pl-12 lg:border-l lg:border-orange-border/40">
                <GuideTrigger theme="orange" open={isGuideOpen} onClick={openGuide} guideTitle={GUIDE_CONTENT.demo.title} className="hidden lg:inline-flex absolute -left-4 top-1/2 -translate-y-1/2 z-30" />
                <m.div className="grid sm:grid-cols-2 gap-x-5 gap-y-4"
                  variants={{ hidden: {}, show: { transition: { staggerChildren: reduce ? 0 : 0.07, delayChildren: reduce ? 0 : 0.12 } } }}
                  initial="hidden" animate="show">
                  {[
                    { Icon: User, id: "qe-name", label: "Your full name", req: true, ph: "Enter your name", val: f.contact_name, key: "contact_name", tid: "enquiry-name" },
                    { Icon: Mail, id: "qe-email", label: "Email", type: "email", req: true, ph: "Enter your email", val: f.email, key: "email", tid: "enquiry-email" },
                    { Icon: Phone, id: "qe-phone", label: "Mobile number", ph: "Enter your mobile number", val: f.phone, key: "phone", tid: "enquiry-phone" },
                    { Icon: Building2, id: "qe-inst", label: "School name", req: true, ph: "Enter school name", val: f.school_name, key: "school_name", tid: "enquiry-institution" },
                    { Icon: MapPin, id: "qe-city", label: "City", ph: "Enter your city", val: f.city, key: "city" },
                  ].map(({ Icon, id, label, req, type, ph, val, key, tid }) => (
                    <m.div key={id} variants={fieldItem}>
                      <Label htmlFor={id} className="mb-2 font-semibold text-ink">{label}{req && <span className="text-danger ml-0.5" aria-hidden="true">*</span>}</Label>
                      <div className="relative">
                        <Icon className="w-4 h-4 text-orange/70 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
                        <Input id={id} className="h-12 pl-11" type={type} value={val} onChange={(e) => set(key, e.target.value)} placeholder={ph} {...(tid ? { "data-testid": tid } : {})} {...(req ? { required: true } : {})}
                          aria-invalid={fieldErrors[key] ? true : undefined} aria-describedby={fieldErrors[key] ? `${id}-err` : undefined} />
                      </div>
                      {fieldErrors[key] && <p id={`${id}-err`} role="alert" className="mt-1.5 text-xs font-medium text-danger">{fieldErrors[key]}</p>}
                    </m.div>
                  ))}
                  <m.div variants={fieldItem}>
                    <Label htmlFor="qe-msg" className="mb-2 font-semibold text-ink">Message (optional)</Label>
                    <div className="relative">
                      <MessageSquare className="w-4 h-4 text-orange/70 absolute left-3.5 top-3.5 pointer-events-none" aria-hidden="true" />
                      <Textarea id="qe-msg" className="pl-11 min-h-[48px] h-12 py-3" value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="How can we help you?" />
                    </div>
                  </m.div>
                  {/* CRO-8: light qualification — one optional row so sales stops sizing every
                      lead by phone from zero. Neither select gates submit. */}
                  <m.div variants={fieldItem}>
                    <Label htmlFor="qe-students" className="mb-2 font-semibold text-ink">Approximate number of students (optional)</Label>
                    <Select id="qe-students" className="h-12" value={studentBand} onChange={(e) => setStudentBand(e.target.value)} data-testid="enquiry-students">
                      <option value="">Select a range</option>
                      {LEAD_STUDENT_BANDS.map((band) => <option key={band.value} value={band.value}>{band.label}</option>)}
                    </Select>
                  </m.div>
                  <m.div variants={fieldItem}>
                    <Label htmlFor="qe-role" className="mb-2 font-semibold text-ink">Your role (optional)</Label>
                    <Select id="qe-role" className="h-12" value={role} onChange={(e) => setRole(e.target.value)} data-testid="enquiry-role">
                      <option value="">Select your role</option>
                      {LEAD_ROLE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                    </Select>
                  </m.div>
                </m.div>
                <Captcha onToken={setCaptchaToken} action="demo_enquiry" className="flex justify-center mt-4" />
                <m.div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2 mt-5" initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: reduce ? 0 : 0.5 }}>
                  {err && <span className="text-xs text-danger mr-auto" role="alert">{err}</span>}
                  {/* A11Y-2: AA-proven warm gradient (white 14px/500 needs >=4.5:1 at EVERY stop —
                      see the ORANGE_CTA_GRADIENT math above; the old #F97316→#FB923C was 2.80/2.26). */}
                  <Button type="submit" loading={busy} className="group !rounded-full !px-7 !bg-gradient-to-r !from-[#C2410C] !to-[#9A3412] hover:!from-[#9A3412] hover:!to-[#7C2D12] !border-transparent !text-white shadow-[0_12px_30px_rgba(154,52,18,0.30)] hover:shadow-[0_16px_40px_rgba(154,52,18,0.38)] hover:-translate-y-0.5 transition-[color,background-color,background-image,border-color,box-shadow,transform] duration-300" data-testid="enquiry-submit">{CTA_SECONDARY_LABEL} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></Button>
                </m.div>
              </form>
            </div>
            )}
            {/* footer trust row — fills the space under the form + ties the two panels together */}
            <div className="mt-6 pt-5 border-t border-line grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3">
              {(isReg ? [
                [Zap, "Set up in minutes", "Self-serve — no sales call required", "text-accent", "bg-accent/10"],
                [ShieldCheck, "Free trial included", "No credit card needed to start", "text-positive", "bg-positive-soft"],
                [Users, "Bring everyone on board", "Invite staff, parents & students anytime", "text-orange", "bg-orange/10"],
              ] : [
                [Users, "School-specific review", "Share your institution and rollout needs", "text-orange", "bg-orange/10"],
                [ShieldCheck, "Purpose-limited enquiry", "Your details support this request", "text-caution", "bg-caution-soft"],
                [Clock, "Reviewed follow-up", "Our team shares the next appropriate step", "text-positive", "bg-positive-soft"],
              ]).map(([Icon, t, sub, fg, bg]) => (
                <div key={t} className="flex items-center gap-2.5">
                  <span className={cn("w-9 h-9 rounded-full grid place-items-center shrink-0", bg)}><Icon className={cn("w-4 h-4", fg)} aria-hidden="true" /></span>
                  <div className="min-w-0"><p className="text-[13px] font-semibold text-ink leading-snug">{t}</p><p className="text-[11.5px] text-ink-2 leading-snug">{sub}</p></div>
                </div>
              ))}
            </div>
            <ProcessGuidePanel open={isGuideOpen} onClose={closeGuide} onCta={guideCta} mode={mode} />
            </>
          )}
        </m.div>
      </div>
    </section>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();
  const pid = useId();
  return (
    <div className="group rounded-2xl border border-line bg-surface shadow-card px-5 py-4 hover:border-accent/40 hover:bg-muted/40 transition-colors" data-testid="faq-item">
      <h3 className="m-0">
        <button type="button" aria-expanded={open} aria-controls={pid}
          onClick={() => { if (!open) track("faq_open", { faq_question: String(q).slice(0, 80) }); setOpen((o) => !o); }}
          className="w-full font-heading font-semibold text-ink cursor-pointer flex justify-between items-center gap-3 text-[15px] text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40">
          {q}
          <span className="w-8 h-8 rounded-lg bg-muted grid place-items-center shrink-0 group-hover:bg-accent/10 transition-colors"><ChevronDown className={cn("w-4 h-4 text-accent transition-transform duration-300", open && "rotate-180")} aria-hidden="true" /></span>
        </button>
      </h3>
      <AnimatePresence initial={false}>
        {open && (
          <m.div id={pid} role="region" key="content" className="overflow-hidden"
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={reduce ? undefined : { height: "auto", opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}>
            <p className="text-sm text-ink-2 mt-3 leading-relaxed">{a}</p>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Accessible "Explore" dropdown for the desktop navbar (Esc + click-outside dismiss, roving focus).
// Smoothly scroll to an on-page section by id (honors reduced-motion). The standalone /mobile-app page
// was retired, so "Mobile App" in the Explore menu now jumps to the on-page mobile section instead.
function smoothScrollToId(id) {
  const el = typeof document !== "undefined" ? document.getElementById(id) : null;
  if (!el) return;
  const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
}

// "Features" nav → the hero dashboard showcase. On desktop the hero is a pinned 230vh scroll-reveal,
// so a plain anchor would land on the copy; instead scroll to ~72% progress where the dashboard is
// fully revealed. On mobile / reduced-motion the dashboard is a normal element — scroll it into view.
function scrollToDashboard() {
  if (typeof window === "undefined") return;
  const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const pinned = window.innerWidth >= 1024 && !reduce;
  if (pinned) {
    const el = document.getElementById("dashboard-showcase");
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY;
    const dist = Math.max(0, el.offsetHeight - window.innerHeight);
    window.scrollTo({ top: Math.max(0, top + dist * 0.72), behavior: "smooth" });
  } else {
    const dv = document.getElementById("dashboard-view");
    if (dv) dv.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }
}


function ExploreMenu({ light = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); triggerRef.current?.focus(); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const reduce = useReducedMotion();
  // Real WAI-ARIA menu keyboard behavior (P1-6 — role="menu" without arrow keys is worse than no
  // role): ArrowUp/Down cycle focus through menuitems, Home/End jump, Tab closes and moves on,
  // ArrowDown on the trigger opens + focuses the first item. Esc (above) closes + restores focus.
  const focusItem = (idx) => {
    const items = itemRefs.current.filter(Boolean);
    if (!items.length) return;
    items[((idx % items.length) + items.length) % items.length]?.focus();
  };
  const onMenuKey = (e) => {
    const items = itemRefs.current.filter(Boolean);
    const cur = items.indexOf(document.activeElement);
    if (e.key === "ArrowDown") { e.preventDefault(); focusItem(cur + 1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); focusItem(cur - 1); }
    else if (e.key === "Home") { e.preventDefault(); focusItem(0); }
    else if (e.key === "End") { e.preventDefault(); focusItem(items.length - 1); }
    else if (e.key === "Tab") setOpen(false);
  };
  const onTriggerKey = (e) => {
    if (e.key === "ArrowDown" || (!open && (e.key === "Enter" || e.key === " "))) {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => focusItem(0));
    } else if (e.key === "Tab" && open) {
      // A11Y-8: Tab from the open trigger moves focus onward — close the menu so focus never
      // lands behind a still-open floating panel (menuitems already do this via onMenuKey).
      setOpen(false);
    }
  };
  return (
    <div className="relative" ref={ref}>
      {/* Phase-1 handoff (2026-08-18): the trigger reads "Product" — "Explore" said nothing
          about WHAT the menu holds. The established nav-explore testids stay stable. */}
      <button type="button" ref={triggerRef} onClick={() => setOpen((o) => !o)} onKeyDown={onTriggerKey} aria-haspopup="menu" aria-expanded={open}
        className={cn("px-3.5 py-2 rounded-full text-[15px] font-medium inline-flex items-center gap-1 transition-colors", light ? "text-white/80 hover:bg-white/10 hover:text-white" : "text-ink-2 hover:bg-accent/10 hover:text-accent")} data-testid="nav-explore">
        Product <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      <AnimatePresence>
        {open && (
          <m.div role="menu" aria-label="Product" data-testid="nav-explore-menu" onKeyDown={onMenuKey}
            className="absolute left-1/2 -translate-x-1/2 top-full mt-2.5 w-64 rounded-2xl border border-line bg-surface/90 backdrop-blur-2xl shadow-[0_16px_50px_-10px_rgba(15,23,42,0.22)] p-2 z-50"
            initial={reduce ? false : { opacity: 0, y: -6, scale: 0.96 }} animate={reduce ? undefined : { opacity: 1, y: 0, scale: 1 }} exit={reduce ? undefined : { opacity: 0, y: -6, scale: 0.96 }} transition={{ duration: 0.16, ease: "easeOut" }} style={{ transformOrigin: "top center" }}>
            {EXPLORE_ITEMS.map(({ to, scrollTo, label, icon: Icon, desc, accent }, i) => {
              const cls = "flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-accent/10 focus-visible:bg-accent/10 focus-visible:outline-none transition-colors";
              const isBlue = accent === "blue";
              const setItemRef = (el) => { itemRefs.current[i] = el; };
              const body = (
                <>
                  <span className={cn("w-8 h-8 rounded-lg grid place-items-center shrink-0 mt-0.5", isBlue ? "bg-primary/10" : "bg-orange/10")}><Icon className={cn("w-4 h-4", isBlue ? "text-primary" : "text-orange")} aria-hidden="true" /></span>
                  <span className="min-w-0"><span className="block font-heading font-semibold text-ink text-sm leading-tight">{label}</span><span className="block text-[12px] text-ink-subtle mt-0.5">{desc}</span></span>
                </>
              );
              return scrollTo ? (
                <button key={scrollTo} type="button" role="menuitem" ref={setItemRef} tabIndex={-1} onClick={() => { setOpen(false); smoothScrollToId(scrollTo); }} data-testid={`nav-explore-${scrollTo}`} className={`w-full text-left ${cls}`}>{body}</button>
              ) : (
                <Link key={to} to={to} role="menuitem" ref={setItemRef} tabIndex={-1} onClick={() => setOpen(false)} data-testid={`nav-explore-${to.slice(1)}`} className={cls}>{body}</Link>
              );
            })}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}


export { heroWmPattern, HeroCursorLensReveal, GUIDE_CONTENT, GuideTrigger, ProcessGuidePanel, QuickEnquiry, isBookDemoIntent, FaqItem, smoothScrollToId, scrollToDashboard, ExploreMenu };
